import { ChangeDetectionStrategy, Component, computed, inject, isDevMode, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CreateSignatureDialogComponent } from '@lib/components/signature/create-signature-dialog/create-signature-dialog.component';
import { FileDocument } from '@lib/models/file-document.model';
import { EtatQpi, StorageFile } from '@lib/models/generated/graphql';
import { QpiViewerPanelService } from '@lib/services/qpi-viewer-panel.service';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { map } from 'rxjs/internal/operators/map';
import { ConnaissanceClientDocumentBase } from '../../../components/connaissance-client-document-base';
import { QPIService } from '../services/qpi.service';
@Component({
  templateUrl: './qpi-viewer-page.component.html',
  selector: 'app-qpi-viewer-page',
  styleUrls: ['./qpi-viewer-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SHARED_IMPORTS],
})
export class QPIViewerPageComponent extends ConnaissanceClientDocumentBase implements OnInit {
  protected override readonly documentName = 'profil investisseur';
  private qpiService = inject(QPIService);
  private router = inject(Router);
  private viewerPanelService = inject(QpiViewerPanelService);
  protected readonly showSendToSignature = this.viewerPanelService.showSendToSignature.asReadonly();
  readonly qpiState = toSignal(this.recueilService.recueilState$.pipe(map(state => state?.qpiEtat)), {
    initialValue: EtatQpi.ACompleter,
  });
  readonly fileUrls = computed<string[]>(() => [this.unsignedDocument()?.url].filter(Boolean) as string[]);
  readonly canSendToSignature = computed<boolean>(
    () =>
      ![
        EtatQpi.EnSignatureApresExpiration.toString(),
        EtatQpi.EnSignaturePremierFois.toString(),
        EtatQpi.EnSignatureApresSigne.toString(),
      ].includes(this.qpiState() ?? '')
  );
  readonly isFetchingDoc = signal<boolean>(false);
  async ngOnInit() {
    this.viewerPanelService.fileMetadataSubject.subscribe(async fileMetadata => {
      await this.loadDocuments(fileMetadata);
    });
  }

  public async loadDocuments(fileMetadata?: StorageFile | null): Promise<void> {
    this.unsignedDocument.set(null);
    this.signedDocument.set(null);
    this.isFetchingDoc.set(true);
    try {
      const unsignedDocument = await this.qpiService.loadUnsignedDocument(this.investisseur1Id(), fileMetadata);
      if (unsignedDocument) {
        this.unsignedDocument.set(unsignedDocument);
      }

      if ([EtatQpi.Signe].includes(this.qpiState() as EtatQpi)) {
        const signedDocument = await this.qpiService.loadSignedDocument();
        if (signedDocument) {
          this.signedDocument.set(signedDocument);
        }
      } else {
        this.signedDocument.set(null);
      }
    } catch (error) {
      if (isDevMode()) {
        console.error('error while loading document: ', error);
      }
    } finally {
      this.isFetchingDoc.set(false);
    }
  }

  public sendToSignature(): void {
    const dialogRef = this.dialogService.openDialog(CreateSignatureDialogComponent, { documents: [this.unsignedDocument()] });
    dialogRef.afterClosed().subscribe(success => {
      if (success) {
        this.investisseurService.getInvestisseur(this.investisseurService.investisseurSig()?.code as string);
        this.closeSlidePanel();
        this.router.navigate([
          `/investisseurs/from-list/${this.investisseurService.investisseurSig()?.code}/connaissance-client/synthese-profil-investisseur`,
        ]);
      }
    });
  }

  public async downloadDocument(fileDocument: FileDocument | null): Promise<void> {
    if (fileDocument) {
      await this.documentService.downloadDocumentFromUrl(fileDocument.url as string, fileDocument.fileName);
    }
  }

  closeSlidePanel() {
    this.viewerPanelService.closeDocumentViewer();
  }

  protected override signatureDisabledExtra(): boolean {
    return this.isDERNotReady() || this.isRecueilNotReady();
  }
}
